/* eslint-disable no-param-reassign */
/**
 * NOTE : appConfig is a window.globalObject. In appInitialise we determine the current theme
 * and set appConfig like this:
 * appConfig = _.extend(appConfig.themes.['base'], appConfig.themes['theme to use']);
 *
 */
import { loadLanguage } from '@sendible/localization';

window.$username = undefined;
window.$password = undefined;
window.$user_id = undefined;
window.globalObject_allAppConfigThemes = undefined;

// // User config variables to be populated by server
window.$current_user = {};
window.loginContext = undefined;
window.dashboard_version = 0;

// all themes will inherit these properties unless they are declared within the theme itself
window.appConfig = {
  themes: {
    base: {
      services: {
        SMSOptIn: {
          service_id: 2319,
        },
        AutoResponders: {
          service_id: 644,
        },
      },
      googleKeys: {
        api_key: 'AIzaSyAwYViNTwwY0GOpSP_hF1TawGsDpJL_oH4',
        oauth: '264374642098.apps.googleusercontent.com',
      },
      messagesBeforeInfinity: 4000,
      isWhiteLabel: false,
      calendarUserThreshold: 19, // max number of users before we cannot fetch all messages.
      mentionsPerPage: 20, // paging defaults for mentions
      messagesPerPage: 30, // paging defaults for messages
      usersPerPage: 90,
      tasksPerPage: 20, // paging defaults for tasks
      maximumImageFileSize: 100,
      isTestEnvironment: window.location.host.indexOf('sendilab') !== -1,
      testDomain: WEBPACK_APP,
      liveDomain: WEBPACK_APP,
      enablePendo: true,
      blankImageUrl: '/s3_assets/sendible-public/images/blank.gif',
      defaultVideoIconUrl: '/s3_assets/sendible-public/images/video-icon.png',
      LOGIN_AUTH_TOKEN: 'login_auth_token',
      maximumImageFileSizeByService: {
        facebook: {
          'image-size': 4,
          'gif-size': 15,
        },
        twitter: {
          'image-size': 5,
          'gif-size': 15,
        },
        google: {
          'image-size': 36,
        },
        linkdedin: {
          'image-size': 20,
        },
        other: {
          'image-size': 5,
        },
      },
      maximumVideoFileSize: 250,
      maximumVideoLength: 3540, /* 59 minutes */
      sessionTimeoutDays: 14,
      base_api_url: WEBPACK_API,
      api_url: `http://${WEBPACK_API}`,
      payment_url: `http://${WEBPACK_API}`,
      logJavaScriptErrors: false,
      logApiErrors: false,
      permissions: [],
      supportUrl: 'https://support.sendible.com',
      onbeforeunload() {
        const { user_id } = loginContext.getDefaultLoginDetails();

        advancedLocalStorage.setItem(
          'activeAccountCookie',
          user_id,
        );

        if (window.globalObject.flags.messageSendInProgress === true) {
          return window.lookup('You currently have a message uploading in the background. Closing the window now will mean you lose this message.');
        }

        if (window.globalObject.lastKeyCode === 8) {
          return window.lookup('Do you wish to discard any unsaved changes?');
        }
      },
      onunload() {
        _.each(Utils.openWindows, (value) => {
          value.close();
        });
      },
    },
  },
};

// setup themes
window.globalObject_allAppConfigThemes = {
  sendible: {
    applicationId: 'sendible_global',
    title: 'Sendible',
    branding: {
      appTitle: 'Sendible',
      icon: '/s3_assets/logos/webapp/thumb.png',
      favIcon: '/img/favicon2025.svg',
      themeurl: 'sendible',
      avatarDefaultUrl: '/img/logo_2025--logomark-full.svg',
      logo_url: '/img/logo_2025--logomark-full.svg',
    },
  },
};

/**
 * loginContext is a site wide
 * @type {Object}
 */
window.loginContext = {
  baseUser: { // to be populated by server
    dateTimeFormat: 'dddd, D MMMM HH:mm',
    dateFormat: 'dddd, D MMMM',
    preference: 'default',
    week_start_day: 0,
    monitoring: {
      resultsPerPage: 100,
    },
    messages: {
      defaultFilter: 'ALL', // or the user_id, e.g. 5131
    },
    language: (window.navigator.userLanguage || window.navigator.language).split('-')[0],
  },

  // This is the logged in user
  defaultDetails: {
    fullname: null,
    username: null,
    api_key: null,
    user_id: null,
    accessToken: null,
    avatar: null,
    has_payment_method: null,
    isAccountVerified: null,
    enable_ai_assist: null,
  },

  selectedDetails: {
    fullname: null,
    username: null,
    api_key: null,
    user_id: null,
    avatar: null,
    isAccountVerified: null,
    enable_ai_assist: null,
  },

  loadLanguage,

  init(user_details, onLanguageLoaded) {
    this.setCurrentUser(user_details, onLanguageLoaded);
    loginContext.defaultDetails.accessToken = user_details.accessToken;
    loginContext.defaultDetails.has_payment_method = user_details.has_payment_method;

    $current_user.accessToken = user_details.accessToken;

    $current_user.app_datetime_moment_readonly = false;
    $current_user.app_datetime_timezone_readonly = false;
  },

  setCurrentUser(user_details, onLanguageLoaded, preventLanguageLoad, callback) {
    $current_user = _.extend(loginContext.baseUser, user_details);

    if (!$current_user.preference) {
      $current_user.preference = 'default';
    }

    if (!preventLanguageLoad) {
      loginContext.loadLanguage(onLanguageLoaded);
    }

    if ($current_user.clock_format == 12) {
      $current_user.timeFormat = 'h:mm a'; // HH:mm and h:mm a
      $current_user.militaryTime = false;
    } else {
      $current_user.timeFormat = 'HH:mm'; // HH:mm and h:mm a
      $current_user.militaryTime = true;
    }

    window.$username = $current_user.login;
    window.$password = $current_user.api_key;

    loginContext.setDefaultLoginDetails(user_details);

    $current_user.preference = loginContext.defaultDetails.preference;

    if (typeof callback !== 'undefined') {
      callback();
    }
  },

  getWeekStartDay() {
    const SUNDAY_INDEX = 0;
    return loginContext.defaultDetails.user_week_start_day ?? loginContext.defaultDetails.company_week_start_day ?? SUNDAY_INDEX;
  },

  setCompanyWeekStartDay(dayIndex) {
    if (dayIndex > 1) {
      return;
    }
    loginContext.defaultDetails.company_week_start_day = dayIndex;
  },

  setAccountVerification(isAccountVerified) {
    if (loginContext.isLoggedInUserActive()) {
      loginContext.defaultDetails.isAccountVerified = isAccountVerified;
    } else {
      loginContext.selectedDetails.isAccountVerified = isAccountVerified;
    }
  },
  setEnableAIAssist(enableAIAssist) {
    if (loginContext.isLoggedInUserActive()) {
      loginContext.defaultDetails.enable_ai_assist = enableAIAssist;
    } else {
      loginContext.selectedDetails.enable_ai_assist = enableAIAssist;
    }
  },
  setBillingCreditsInfo(accountInfo) {
    if (loginContext.isLoggedInUserActive()) {
      loginContext.defaultDetails.accountInfo = accountInfo;
    } else {
      loginContext.selectedDetails.accountInfo = accountInfo;
    }
  },

  getBillingCreditsInfo() {
    if (loginContext.isLoggedInUserActive()) {
      return loginContext.defaultDetails.accountInfo;
    }
    return loginContext.selectedDetails.accountInfo;
  },

  setCurrentFullName(fname) {
    if (loginContext.isLoggedInUserActive()) {
      loginContext.defaultDetails.fullname = fname;
    } else {
      loginContext.selectedDetails.fullname = fname;
    }
  },

  setCurrentLoginDetails(oDetails) {
    if (loginContext.isLoggedInUserActive()) {
      this.setCurrentUser(oDetails, null, true);
    } else {
      this.setSelectedtLoginDetails(oDetails);
    }
  },

  setDefaultLoginDetails(oDetails) {
    loginContext._setContext(loginContext.defaultDetails, oDetails);
  },

  setSelectedtLoginDetails(oDetails, callback) {
    window.$username = oDetails.username;
    window.$password = oDetails.api_key;
    window.$user_id = oDetails.user_id;

    loginContext._setContext(loginContext.selectedDetails, oDetails);

    if (typeof callback !== 'undefined') {
      callback();
    }
  },

  setDetailsParameter(parameter, value) {
    if (loginContext.isLoggedInUserActive()) {
      if (typeof loginContext.defaultDetails[parameter] !== 'undefined') {
        loginContext.defaultDetails[parameter] = value;
      }
    } else if (typeof loginContext.selectedDetails[parameter] !== 'undefined') {
      loginContext.selectedDetails[parameter] = value;
    }
  },

  getDefaultLoginDetails() {
    return loginContext.defaultDetails;
  },

  getSelectedLoginDetails() {
    return loginContext.selectedDetails;
  },

  // getLoginContextForAPI: if no params passed in then use the current selected user
  getLoginContextForAPI(useApiKey, useToken) {
    const selected = loginContext.selectedDetails;
    let defaultD;

    // if user has selected another user's login details use them
    // else use default login details.
    if (loginContext.isLoggedInUserActive() || useToken) {
      defaultD = loginContext.defaultDetails;
      return `username=${defaultD.username}&access_token=${defaultD.accessToken}`;
    } if (!loginContext.isLoggedInUserActive() || useApiKey) {
      return `username=${selected.username
      }&api_key=${selected.api_key
      }&logged_in_username=${loginContext.defaultDetails.username
      }&logged_in_access_token=${loginContext.defaultDetails.accessToken}`;
    }
  },

  // getCurrentDetails returns either the logged in user details or the selected user details depending on the active user.
  getCurrentDetails() {
    if (!storageUtils.getCookieOrLocalStorageItem(`s_accessToken_${loginContext.getDefaultLoginDetails().user_id}`)) {
      this.logoutAllUsers();
    } else {
      if (loginContext.isLoggedInUserActive()) {
        return loginContext.getDefaultLoginDetails();
      }
      return loginContext.getSelectedLoginDetails();
    }
  },

  removeSelected() {
    const selected = loginContext.selectedDetails;
    // eslint-disable-next-line
    for (let key in selected) { 
      selected[key] = null;
    }
  },

  signout() {
    Sendible.Connect.logout(
      null,
      () => {
        Utils.removeAllCookies();
        window.localStorage?.clear();

        window.location.reload();
      },
    );

    window.onbeforeunload = function () {
      _.each(
        Utils.openWindows,
        (value) => {
          value.close();
        },
      );
    };
  },

  resetAccountShowLogin(setEmailFieldCookie) {
    advancedLocalStorage.removeItem('activeAccountCookie');
    advancedLocalStorage.removeItem('s_initialFragment');

    if (setEmailFieldCookie) {
      advancedLocalStorage.setItem('s_loginForm_emailFieldSet', $current_user.email, 14);
    }

    window.onbeforeunload = function () {
      _.each(Utils.openWindows, (value) => {
        value.close();
      });
    };
  },

  /**
   * @deprecated look to use the window.loginContext.signout() function instead.
   */
  loginAsOther() {
    loginContext.resetAccountShowLogin(true);
  },

  /**
   * @deprecated look to use the window.loginContext.signout() function instead.
   */
  logoutAllUsers() {
    advancedLocalStorage.removeItem(`s_accessToken_${loginContext.getDefaultLoginDetails().user_id}`);
    loginContext.resetAccountShowLogin(true);
    window.location.reload();
  },

  /**
   * @deprecated look to use the window.loginContext.signout() function instead.
   */
  logoutAllUsersAndResetCache(callback) {
    advancedLocalStorage.removeItem(`s_accessToken_${loginContext.getDefaultLoginDetails().user_id}`);
    Utils.resetCache();
    loginContext.resetAccountShowLogin(false);

    if (callback) {
      callback();
    }

    window.location.reload();
  },

  isLoggedInUserActive() {
    const selected = loginContext.selectedDetails;

    // if user has selected another users login details use them
    // else use default login details.
    if (selected.username !== null && selected.api_key !== null) {
      return false;
    }

    return true;
  },

  // _setContext is an internal use only function. it sets param 'details' with user details from parame 'oDetails'.
  // if the logged in user is the same as the selected user then removeSelected is run which will reset the selectedDetails.
  _setContext(details, oDetails) {
    const lc = loginContext;
    details.username = oDetails.login;

    if (lc.selectedDetails.username === lc.defaultDetails.username
                && (typeof lc.selectedDetails.username !== 'undefined' && typeof lc.defaultDetails.username !== 'undefined')) {
      lc.removeSelected();
      return;
    }

    details = _.extend(details, oDetails);
    details.username = oDetails.login;
    details.user_id = oDetails.id;

    if (!details.preference) {
      details.preference = 'default';
    }

    if (details.clock_format == 12) {
      details.timeFormat = 'h:mm a'; // HH:mm and h:mm a
      details.militaryTime = false;
    } else {
      details.timeFormat = 'HH:mm'; // HH:mm and h:mm a
      details.militaryTime = true;
    }
  },
};
